import { useForm, useWatch } from "react-hook-form"
import { BLOCK, COMMUNITY, OCCUPANCY, OUTSTANDING_DUES, SEARCH, STATUS, ZONE } from "./consts"
import { useGetCommunitiesDropdown, useGetOwnersReport, useGetOwnersReportExport, useGetPropertyBlocks, useGetPropertyZones } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge, Button } from "@/components/ui";
import { cn, formatAmount, toApiSort } from "@/lib/utils";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useOwnersReport = () => {  
  const form = useForm<any>({
    defaultValues: {
      [COMMUNITY]: ["all"],
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [OCCUPANCY]: ["all"],
      [OUTSTANDING_DUES]: false,
      [SEARCH]: ""
    }
  });
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("all");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [openCommunityDialog, setOpenCommunityDialog] = useState<boolean>(false);
  const [openPropertyDialog, setOpenPropertyDialog] = useState<boolean>(false);
  const [openOutstandingDialog, setOpenOutstandingDialog] = useState<boolean>(false);
  const communityId = useWatch({ control: form.control, name: COMMUNITY });
  const formData = useWatch({ control: form.control });
  const outstandingDues = useWatch({ control: form.control, name: OUTSTANDING_DUES });
  const selectedRow = useRef<any>(null);
  const queryClient = useQueryClient();
  
  const filter = useMemo(() => ({
    [OUTSTANDING_DUES]: formData[OUTSTANDING_DUES],
    pageNumber,
    pageSize,
    [SEARCH]: formData[SEARCH],
    sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    ...(formData[COMMUNITY]?.includes("all") ? {} : {[COMMUNITY]: formData[COMMUNITY]}),
    ...(formData[ZONE]?.includes("all") ? {} : {[ZONE]: formData[ZONE]}),
    ...(formData[BLOCK]?.includes("all") ? {} : {[BLOCK]: formData[BLOCK]}),
    ...(formData[OCCUPANCY]?.includes("all") ? {} : {[OCCUPANCY]: formData[OCCUPANCY]})
  }), [communityId, pageNumber, pageSize, formData, sorting]);

  const { data: communityList, isLoading: isLoadingCommunity } = useGetCommunitiesDropdown({});

  const { data: zoneList, isRefetching: isLoadingZoneList } = useGetPropertyZones();

  const { data: propertyBlock, isRefetching: isBlockLoading } = useGetPropertyBlocks();

  const onTabChange = (tab: any) => {
    setActiveTab(tab);
    setTimeout(() => refetchReportData(), 100);
  }

  const columns = [
    {
      accessorKey: "userName",
      header: <div>Owner/User ID</div>,
      cell: ({row}: any) => {
        const { ownerName, userName } = row.original;
        return <div className="flex flex-col">
          <span className="text-[14px] ">{ownerName || ""}</span>
          <span className="text-[12px] text-gray-500">User - {userName || ""}</span>
        </div>
      }
    },
    {
      accessorKey: "mobileNo",
      header: <div>Mobile No.</div>,
      accessorFn: (row: any) => String(row?.mobileNo || ""),
    },
    {
      accessorKey: "communities",
      header: <div>Community Name</div>,
      cell: ({row}: any) => {
        const { communities } = row?.original;

        return communities?.length ? <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = row.original;
          setOpenCommunityDialog(true);
        }}>
          <span>{communities?.[0]?.communityName || ""} {communities?.length > 1 ? `+ ${communities?.length - 1}` : ""}</span>
        </Button> : <span>{communities?.length}</span>
      },
    },
    {
      accessorKey: "properties",
      header: <div>Properties</div>,
      cell: ({row}: any) => {
        const { properties } = row?.original;

        return properties.length ? <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = row.original;
          setOpenPropertyDialog(true);
        }}>
          <span>{properties?.length}</span>
        </Button> : <span>{properties?.length}</span>
      },
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({ row }: any) => {
        const isActive = row?.original?.status == "ACTIVE";
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive ? "text-green-600 border-green-600 bg-green-50" : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "outstandingDues",
      header: <div>Outstanding Dues</div>,
      cell: ({row}: any) => {
        const { outstandingDues } = row?.original;

        return outstandingDues ? <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = row.original;
          setOpenOutstandingDialog(true);
        }}>
          {formatAmount(outstandingDues || "0")}
        </Button> : <span>{formatAmount(outstandingDues || "0")}</span>
      },
    },
  ];

  const onOutstandingCheckedChange = (checked: boolean) => {
    form.setValue(OUTSTANDING_DUES, checked);
    setPageNumber(0);
    setTimeout(() => refetchReportData(), 100);
  }

  const onClearAll = () => {
    form.reset({
      [COMMUNITY]: ["all"],
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [OCCUPANCY]: ["all"],
      [OUTSTANDING_DUES]: false,
      [SEARCH]: ""
    });
  }

  const { data: reportData, isRefetching: isLoadingReport, refetch: refetchReportData } = useGetOwnersReport({
    data: { 
      ...filter, 
      ...(activeTab !== "all" && { [STATUS]: activeTab.toUpperCase() }),
    },
    queryParams: {
      enabled: false
    }
  });

  const { data: reportExportData, isRefetching: isLoadingExportReport, refetch: refetchExportReportData } = useGetOwnersReportExport({
    data: { 
      ...filter, 
      ...(activeTab !== "all" && { [STATUS]: activeTab.toUpperCase() }),
    },
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (!reportExportData) return;

    try {
      const blob = new Blob([reportExportData.data], {
        type: reportExportData.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = reportExportData.headers["content-disposition"];
      let downloadFileName = `owner-report.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^\"]+)"?/);
        if (match?.[1]) downloadFileName = match[1];
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "There is an issue while downloading the file"
      );
    } finally {
      queryClient.removeQueries({
        queryKey: ["owners-report-export"],
      });
    }
  }, [reportExportData]);

  const onExport = () => {
    refetchExportReportData();
  }

  const onSubmit = () => {
    refetchReportData();
  }

  useEffect(() => {
    refetchReportData();
  }, [pageNumber, pageSize, sorting])

  return {
    form,
    isLoadingCommunity,
    isLoadingZoneList,
    isBlockLoading,
    communityList: communityList || [],
    zoneList: zoneList?.data || [],
    propertyBlock: propertyBlock?.data || [],
    activeTab, onTabChange, isMobile,
    columns,
    onOutstandingCheckedChange,
    outstandingDues,
    onClearAll,
    onSubmit,
    isLoadingReport,
    reportData,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    sorting,
    setSorting,
    openCommunityDialog,
    setOpenCommunityDialog,
    openOutstandingDialog,
    setOpenOutstandingDialog,
    selectedRow: selectedRow.current,
    openPropertyDialog,
    setOpenPropertyDialog,
    formData,
    onExport,
    isLoadingExportReport
  }
}

export default useOwnersReport;